/* eslint-disable jira/filename-case, jira/naming/disallow-filenames */
import React, { memo, useMemo } from 'react';

// eslint-disable-next-line jira/restricted/redux
import type { Store } from 'redux';
import { useMatch } from '@jira/navigation-apps__routing-hooks/src/useMatch.tsx';
import type { State } from '@jira/portfolio-3__portfolio/src/app-simple-plans/state/types.tsx';
import Placeholder from '@atlassian/jira-placeholder/src/index.tsx';
import { lazyAfterPaint } from '@atlassian/react-loosely-lazy';
import { useTenantContext } from '@atlassian/jira-tenant-context-controller/src/components/tenant-context/index.tsx';

import { Skeleton } from './skeleton/index.tsx';

const LazyPlanSettingsSidebar = lazyAfterPaint(
	() =>
		import(
			/* webpackChunkName: "async-jira-navigation-apps-sidebar-nav4-sidebar-plan-settings" */ './PlanSettingsSidebar.tsx'
		).then(({ PlanSettingsSidebar }) => PlanSettingsSidebar),
	{
		entryFsSsrLiftImportToModule: false, // can't be true yet as plans use window.location.pathname at module level & create redux reducers at module level - both not import lifting friendly
		includeInShellOnlySsrBundle: true,
	},
);

type Props = {
	store?: Store<State>;
};

const toPlanAri = ({
	siteId,
	activationId,
	planId,
}: {
	siteId: string;
	activationId: string;
	planId: string;
}): string => `ari:cloud:jira:${siteId}:plan/activation/${activationId}/${planId}`;

const AsyncSidebar = ({ store }: Props): React.JSX.Element => {
	const { params } = useMatch();
	const { cloudId, activationId } = useTenantContext();

	const { planId } = params;
	const planIdAri = useMemo(
		() =>
			toPlanAri({
				siteId: cloudId,
				activationId,
				planId: String(planId),
			}),
		[cloudId, activationId, planId],
	);

	return (
		<Placeholder name="planSettingsSidebar" fallback={<Skeleton />}>
			<LazyPlanSettingsSidebar store={store} planIdAri={planIdAri} planId={String(planId)} />
		</Placeholder>
	);
};

const ExportedComponent: React.NamedExoticComponent<Props> = memo(AsyncSidebar);
export default ExportedComponent;
